import React from 'react';

// Deterministic pseudo-random generator from a string seed
function seededRandom(seed: string) {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return () => {
    h += 0x6d2b79f5;
    let t = Math.imul(h ^ h >>> 15, 1 | h);
    t ^= t + Math.imul(t ^ t >>> 7, 61 | t);
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
}
interface WaveformProps {
  seed: string;
  bars?: number;
  className?: string;
  thickness?: number;
  rounded?: boolean;
}

/**
 * Deterministic audio-waveform preview.
 * Renders as an inline SVG that fills its container, takes color from
 * currentColor, and generates the same shape every time for a given seed.
 */
export function Waveform({
  seed,
  bars = 96,
  className = '',
  thickness = 2,
  rounded = true
}: WaveformProps) {
  const rand = seededRandom(seed);
  // Two overlaid envelopes to make it feel less uniform — a low-frequency
  // amplitude wave multiplied by a per-bar noise term.
  const heights = React.useMemo(() => {
    const out: number[] = [];
    for (let i = 0; i < bars; i++) {
      const envelope = 0.5 + 0.35 * Math.sin(i / bars * Math.PI * (2 + rand())) + 0.15 * Math.sin(i / bars * Math.PI * 7);
      const noise = 0.3 + Math.pow(rand(), 1.4) * 0.8;
      const h = Math.min(1, Math.max(0.08, envelope * noise));
      out.push(h);
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seed, bars]);
  const gap = 2;
  const barW = thickness;
  const stride = barW + gap;
  const width = bars * stride;
  const height = 48;
  return <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" className={`block w-full h-full ${className}`} aria-hidden="true" data-id="element-124">
      {heights.map((h, i) => {
      const hpx = h * (height - 4) + 2;
      const y = (height - hpx) / 2;
      return <rect key={i} x={i * stride} y={y} width={barW} height={hpx} rx={rounded ? barW / 2 : 0} fill="currentColor" data-id="element-125" />;
    })}
    </svg>;
}